// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * Decree 252/2026 intake (S4, 2026-10-26) — what a deadline's
 * `excluded_periods` may name. A property of the activity, never an
 * instrument's value (R-46): the time a party spends answering the
 * authority's own request for an explanation or for more information is not
 * the authority's time.
 *
 * Law 108/2025 intake (S4, 2026-10-27) — `authority_notified_suspension`: the
 * time an activity stands paused under a pause the authority itself notifies
 * (an inspection "tạm dừng ... theo thông báo"), which the stated period does
 * not count. Declared so an inspection clock can say what it excludes; no
 * reader measures the interval yet, so a reader must not show the counted end
 * as the end (the contract above).
 */
export const DEADLINE_EXCLUDED_PERIOD_KINDS = [
    'taxpayer_explanation_or_supplement',
    'authority_notified_suspension',
];
// ── Key lists — each checked against the type at compile time ─────
const INSERT_ONLY_KEY_SET = {
    _id: true,
    created_at: true,
};
const INSTALLER_KEY_SET = {
    org_id: true,
    framework_id: true,
    framework_code: true,
    deadline_id: true,
    installed_from_pack: true,
    updated_at: true,
    created_by: true,
};
/**
 * `Record<InstalledPackDeadlineAuthoredKey, true>` is the compile-time
 * exhaustiveness check in both directions: a key the type gains and this object
 * lacks is a missing-property error, and a key this object has and the type
 * lacks is an excess-property error.
 */
const AUTHORED_KEY_SET = {
    sla_type: true,
    deadline: true,
    unit: true,
    extension: true,
    reference: true,
    absolute_date: true,
    status: true,
    evidence_required: true,
    linked_form_id: true,
    linked_procedure_id: true,
    triggered_by_phased_obligation_code: true,
    note: true,
    event_type: true,
    request_types: true,
    coordination_mode: true,
    applies_to_activity: true,
    applies_only_if: true,
    related_form_schema_ids: true,
    trigger_event: true,
    register_surface: true,
    applies_to_tiers: true,
    event_trigger: true,
    contents: true,
    applies_to_source_entity: true,
    obligation_owner: true,
    automation_status: true,
    post_stop_duty: true,
    post_stop_scope: true,
    post_stop_anchor: true,
    request_type_advisories: true,
    received_event_id: true,
    received_state_id: true,
    confirmation_event_id: true,
    confirmation_state_id: true,
    source_status: true,
    lapse_consequence: true,
    title: true,
    trigger_evidence_required: true,
    calendar_kind: true,
    review_state: true,
    request_type_labels: true,
    issuing_authority: true,
    window_direction: true,
    release_prerequisites: true,
    gate_citation: true,
    identifier_label: true,
    window_role: true,
    review_required_request_types: true,
    condition_sets: true,
    subject_identifier_ref_key: true,
    subject_identifier_ref_label: true,
    tier_rule_conditions: true,
    recurrence_month: true,
    recurrence_day: true,
    absolute_date_status: true,
    source_verified: true,
    source_verification_note: true,
    period_basis: true,
    requires_official_confirmation: true,
    source: true,
    superseded_by_framework_code: true,
    time_of_day: true,
    interruption_threshold_minutes: true,
    non_working_day_rule: true,
    calendar_policy: true,
    register_clock_role: true,
    register_clock_anchor: true,
    excluded_periods: true,
    applies_to_window_ids: true,
    legal_status: true,
    owner_question: true,
    legal_status_note: true,
    applies_to_assessment_kinds: true,
    cure_triggers: true,
    deficiency_text: true,
    citation: true,
    start_event_note: true,
    end_roll: true,
    fine_area_refs: true,
    fine_area_basis: true,
    applies_to_procedure_ids: true,
    period_role: true,
    period_trigger: true,
    period_label: true,
    owner_label: true,
    precaution: true,
};
/**
 * The keys the installer copies from an authored deadline record. The installer
 * iterates this list; it does not enumerate fields of its own.
 */
export const INSTALLED_PACK_DEADLINE_AUTHORED_KEYS = Object.freeze(Object.keys(AUTHORED_KEY_SET));
/** The keys the installer sets itself in `$set`. */
export const INSTALLED_PACK_DEADLINE_INSTALLER_KEYS = Object.freeze(Object.keys(INSTALLER_KEY_SET));
/** The keys written only on insert (`$setOnInsert`). */
export const INSTALLED_PACK_DEADLINE_INSERT_ONLY_KEYS = Object.freeze(Object.keys(INSERT_ONLY_KEY_SET));
/**
 * Keys packs author and the installer deliberately does not persist, each with
 * the reason (Sprint 62.25.0 AC3; work file D-AC3). Replaces the fidelity
 * test's `KNOWN_UNPERSISTED_KEYS`, which named keys and carried no reasons.
 *
 * **It cannot shrink to empty.** `is_compliance_deadline` is denied by an owner
 * ruling (OD10), and each dialect or collision key below has a stated reason
 * not to reach the installed row.
 *
 * A key moves from here to persisted only when `InstalledPackDeadline` gains it
 * and its entry here is deleted in the same commit. The two lists are disjoint
 * by construction — a key on both fails `tsc` (see `_DeniedKeysAreNotInstalled`
 * below) — and every entry is authored by some pack, which the fidelity test
 * asserts over all three installer roots.
 */
export const INSTALLED_PACK_DEADLINE_DENIED_KEYS = {
    deadline_code: {
        reason: 'Identity dialect. The installer normalises it into `deadline_id` (62.13.0 AC1); persisting it as well would give readers two identity fields for one record.',
        superseded_by: 'deadline_id',
    },
    // Sprint 63.33.0 — seven dialect keys left this list because no pack authors
    // them any more (the fidelity test's `stale` rule). Each record that did had
    // stated its period, cadence or forms ONLY in them, so its installed row
    // carried none of it (`DEADLINE_PERIOD_ONLY_IN_DENIED_KEY`, and the census in
    // `scripts/deadline-denied-key-census.ts`). Those records now author the
    // persisted field:
    //   `day` / `month` / `deadline_type` → `recurrence_day` / `recurrence_month`
    //     with `unit: annual_by_date` (the EPR overlay's three annual dates);
    //   `offset_days` → `deadline`, `offset_type` → `unit`, `timer_anchor` →
    //     `trigger_event` (Circular 83's three report clocks);
    //   `applicable_form_ids` → `related_form_schema_ids` (Circular 83's four).
    // A pack that authors any of them again is refused by `validate:packs`
    // (`DEADLINE_KEY_UNKNOWN`). Re-deny one only with the reason the installer
    // refuses it AND a persisted field the fact moves to.
    // `deadline_kind`, `trigger` and `applicability` left this list in Sprint
    // 63.33.0. Their last authors were two `vn-cybersecurity` records (Law 116
    // Điều 25 khoản 2 điểm b, the 24-hour and 06-hour content-removal clocks),
    // whose selector, trigger and scope reached no tenant. Read from the text,
    // both are authority-direction clocks (they run from the specialised force's
    // request, not from an incident): they now author `event_type:
    // authority_direction_response` with `request_types`, `trigger_event:
    // authority_request`, and their scope in the bilingual `note`.
    // `target_date` was denied here until Sprint 63.33.0. Its only author, the
    // Law 116 Điều 45 khoản 3 record in `vn-cybersecurity`, now states that date
    // as `absolute_date` with its counting basis, so no pack authors the key and
    // the entry was a registration of nothing (the fidelity test's `stale` rule).
    // `recurrence` (English-only prose cadence) left this list in Sprint 63.33.0:
    // four records stated their annual date only there, and five repeated a
    // cadence their `recurrence_month` / `recurrence_day` and bilingual `note`
    // already carry. All nine now carry it in those fields.
    // `applies_to` (English-only prose scope) left this list in Sprint 63.33.0:
    // 24 records authored it, 23 of them with a scope no kept field carried. Each
    // was read against its instrument; where the bilingual `note` did not already
    // name the addressee or condition, the note now does, and the key is gone.
    // It was not moved into `applies_only_if` or `applies_to_activity`: those are
    // read as conditions (the incident SLA seed does not default a scoped clock;
    // the rights resolver refuses an undeclared precondition), and an addressee
    // statement is not a condition.
    // A pack that authors any of these again is refused by `validate:packs`
    // (`DEADLINE_KEY_UNKNOWN`). Re-deny one only with the reason the installer
    // refuses it AND a persisted field the fact moves to.
    boundary_interpretation: {
        reason: "An authoring decision record — the pack author's reasoning about where a boundary falls. It belongs in the pack and its CHANGELOG; persisting it would carry internal reasoning into tenant rows.",
    },
    // `description` left this list in Sprint 63.33.0: its seven authors (Circular
    // 83's four records and the EPR overlay's three) now carry that prose in
    // `note`, where it reaches the tenant. Do NOT declare `description` on
    // `InstalledPackDeadline` to make a pack that authors it pass: it collides
    // with the UI-authored `RegulatoryDeadline.description: string | null` in the
    // same collection, while packs author a bilingual object, so one field would
    // hold two types. Author `note`; `DEADLINE_KEY_UNKNOWN` refuses the key.
    regime_code: {
        reason: "Collides with the UI-authored `RegulatoryDeadline.regime_code` in the same collection. The installed record's identity is `deadline_id`.",
        superseded_by: 'deadline_id',
    },
    is_compliance_deadline: {
        reason: 'Denied by owner ruling OD10 (product owner, 2026-09-23): "a draft placeholder must not assert an obligation". The authored population also includes a reference-only record, which the same reason covers. A stored flag on the installed row is the first thing a future reader would take as the answer; whether a clock is owed belongs to the framework\'s status and the organisation\'s install mode, not to a per-row flag.',
    },
};
const _deniedKeysAreNotInstalled = true;
void _deniedKeysAreNotInstalled;
//# sourceMappingURL=installed-pack-deadline.js.map